import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../../components/Toast';
import { 
  GraduationCap, 
  Clock, 
  Plus, 
  Trash2, 
  Save, 
  ChevronDown, 
  ChevronUp, 
  Hourglass, 
  Mail, 
  CheckCircle2, 
  Check, 
  Star 
} from 'lucide-react';
import './FormsUnified.css';

const DEFAULT_FORM_DATA = {
  kicker: 'Internships / Volunteering',
  title: 'Opportunities to Learn, Serve, and Grow',
  description: 'Short-term roles that support the school community while building valuable experience and making a meaningful impact.',
  items: [
    {
      id: 'student-teaching',
      title: 'Student Teaching Internship',
      description: 'Gain hands-on teaching experience in a supportive environment with mentorship from experienced educators.',
      highlights: [
        'Classroom observation & co-teaching',
        'Lesson planning & curriculum design',
        'Student assessment & feedback'
      ],
      fullDescription: 'Our Student Teaching Internship program is designed for aspiring educators who want to gain practical classroom experience. You will work alongside experienced teachers, observe best practices, and gradually take on teaching responsibilities under close mentorship.',
      duration: '1-2 semesters',
      commitment: '20-30 hours per week',
      benefits: [
        'Hands-on classroom experience',
        'Mentorship from experienced educators',
        'Professional development workshops',
        'Certificate of completion',
        'Networking opportunities',
        'Potential for future employment'
      ],
      requirements: [
        'Currently enrolled in or recently completed education program',
        'Strong communication skills',
        'Passion for teaching and student development',
        'Reliability and commitment',
        'Background check required'
      ],
      contact: 'hr@permataharapan.sch.id'
    },
    {
      id: 'event-volunteer',
      title: 'Event & Program Volunteer',
      description: 'Support school events, competitions, and community initiatives while building leadership and organizational skills.',
      highlights: [
        'Event coordination & logistics',
        'Student mentoring & support',
        'Community engagement activities'
      ],
      fullDescription: 'Join our dynamic events team and help create memorable experiences for our school community. From academic competitions to cultural celebrations, you\'ll play a key role in organizing and executing events that bring our community together.',
      duration: 'Flexible (per event basis)',
      commitment: '10-20 hours per month',
      benefits: [
        'Event planning experience',
        'Leadership development',
        'Community connection',
        'Volunteer certificate',
        'Social impact',
        'Team collaboration'
      ],
      requirements: [
        'Organizational skills',
        'Enthusiasm and positive attitude',
        'Ability to work in teams',
        'Flexibility with scheduling',
        'Attention to detail'
      ],
      contact: 'events@permataharapan.sch.id'
    },
    {
      id: 'research-volunteer',
      title: 'Research & Innovation Volunteer',
      description: 'Contribute to educational research projects and help develop innovative learning solutions for our students.',
      highlights: [
        'Educational research support',
        'Technology & innovation projects',
        'Data analysis & reporting'
      ],
      fullDescription: 'Be part of our innovation lab where we explore new teaching methodologies and educational technologies. You\'ll contribute to research projects that shape the future of education at our school and potentially beyond.',
      duration: '3-6 months',
      commitment: '15-25 hours per week',
      benefits: [
        'Research experience',
        'Technology skills development',
        'Publication opportunities',
        'Academic recognition',
        'Innovation exposure',
        'Professional growth'
      ],
      requirements: [
        'Research or analytical background preferred',
        'Proficiency with data tools',
        'Critical thinking skills',
        'Attention to detail',
        'Ability to work independently'
      ],
      contact: 'research@permataharapan.sch.id'
    }
  ]
};

export default function CareersOpportunitiesForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);

  const [pendingFields, setPendingFields] = useState({
    kicker: false,
    title: false,
    description: false,
    items: false
  });
  const [pendingItems, setPendingItems] = useState({});
  const [pendingDeletes, setPendingDeletes] = useState({});
  const [originalData, setOriginalData] = useState({ ...formData });

  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [expandedIndex, setExpandedIndex] = useState(0);

  // Inputs for adding highlight, benefit, requirement
  const [newHighlightText, setNewHighlightText] = useState({});
  const [newBenefitText, setNewBenefitText] = useState({});
  const [newReqText, setNewReqText] = useState({});

  const getAuthHeaders = useCallback(() => {
    let token = localStorage.getItem('token') || 
                localStorage.getItem('ph_auth_token') || 
                localStorage.getItem('auth_token') || 
                sessionStorage.getItem('token');
    if (!token) {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        if (u?.token) token = u.token;
      } catch (e) {}
    }
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  }, []);

  const loadSection = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/homepage/cms/sections?page=careers`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });

      let foundSection = null;
      if (res.ok) {
        const json = await res.json();
        const items = Array.isArray(json.data) ? json.data : Object.values(json.data || {});
        foundSection = items.find(s => s.section_type === 'careers-opportunities' || s.section_type === 'internships-volunteering');
      }

      if (!foundSection) {
        const fbRes = await fetch(`${apiBase}/homepage/cms/sections?published=true&page=careers`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        if (fbRes.ok) {
          const fbJson = await fbRes.json();
          const items = Array.isArray(fbJson.data) ? fbJson.data : Object.values(fbJson.data || {});
          foundSection = items.find(s => s.section_type === 'careers-opportunities' || s.section_type === 'internships-volunteering');
        }
      }

      if (foundSection) {
        setSectionId(foundSection.id);
        let content = foundSection.content_published;
        if (!content || content === '{}' || content === '[]') {
          content = foundSection.content;
        }
        if (typeof content === 'string') {
          try {
            content = JSON.parse(content);
          } catch (e) {
            content = {};
          }
        }

        const en = content?.en || content || {};
        const rawItems = Array.isArray(en.items) && en.items.length > 0 
          ? en.items 
          : (Array.isArray(en.internships_volunteering) && en.internships_volunteering.length > 0 ? en.internships_volunteering : DEFAULT_FORM_DATA.items);

        const loaded = {
          kicker: en.kicker || 'Internships / Volunteering',
          title: en.title || 'Opportunities to Learn, Serve, and Grow',
          description: en.description || DEFAULT_FORM_DATA.description,
          items: rawItems.map(item => ({
            id: item.id || '',
            title: item.title || '',
            description: item.description || '',
            highlights: Array.isArray(item.highlights) ? item.highlights : (typeof item.highlights === 'string' ? item.highlights.split('\n').filter(Boolean) : []),
            fullDescription: item.fullDescription || item.full_description || item.description || '',
            duration: item.duration || 'Flexible',
            commitment: item.commitment || 'Varies',
            benefits: Array.isArray(item.benefits) ? item.benefits : (typeof item.benefits === 'string' ? item.benefits.split('\n').filter(Boolean) : []),
            requirements: Array.isArray(item.requirements) ? item.requirements : (typeof item.requirements === 'string' ? item.requirements.split('\n').filter(Boolean) : []),
            contact: item.contact || 'hr@permataharapan.sch.id'
          }))
        };

        setFormData(loaded);
        setOriginalData(loaded);

        try {
          const pendingRes = await fetch(`${apiBase}/cms/section/${foundSection.id}/with-pending`, {
            credentials: 'include',
            headers: getAuthHeaders()
          });
          if (pendingRes.ok) {
            const pData = await pendingRes.json();
            if (pData.success && pData.data?.pending_changes) {
              const pFields = { kicker: false, title: false, description: false, items: false };
              const pItems = {};
              const pDeletes = {};

              pData.data.pending_changes.forEach(c => {
                if (c.status === 'pending') {
                  if (['kicker', 'title', 'description', 'items'].includes(c.field_id)) {
                    pFields[c.field_id] = true;
                  } else if (c.field_id?.includes('items.')) {
                    const m = c.field_id.match(/items\.(\d+)\.(add|update|delete)/);
                    if (m) {
                      const idx = m[1];
                      const op = m[2];
                      if (op === 'delete') {
                        pDeletes[`item_${idx}`] = true;
                      } else {
                        pItems[`item_${idx}`] = true;
                      }
                    } else {
                      const mOld = c.field_id.match(/items\.(\d+)/);
                      if (mOld) pItems[`item_${mOld[1]}`] = true;
                    }
                  }
                }
              });

              setPendingFields(pFields);
              setPendingItems(pItems);
              setPendingDeletes(pDeletes);
            }
          }
        } catch (pe) {
          console.warn('[CareersOpportunitiesForm] Pending check error:', pe);
        }
      }
    } catch (err) {
      console.error('[CareersOpportunitiesForm] Load error:', err);
      toast.error('Gagal memuat data Internships & Volunteering');
    } finally {
      setLoading(false);
    }
  }, [apiBase, getAuthHeaders]);

  useEffect(() => {
    loadSection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleItemChange = (index, field, value) => {
    setFormData(prev => {
      const next = [...prev.items];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, items: next };
    });
  };

  const handleAddHighlight = (idx) => {
    const text = (newHighlightText[idx] || '').trim();
    if (!text) return;
    setFormData(prev => {
      const next = [...prev.items];
      const hls = [...(next[idx].highlights || []), text];
      next[idx] = { ...next[idx], highlights: hls };
      return { ...prev, items: next };
    });
    setNewHighlightText(prev => ({ ...prev, [idx]: '' }));
    toast.success('Poin sorotan kartu ditambahkan');
  };

  const handleRemoveHighlight = (idx, hIdx) => {
    setFormData(prev => {
      const next = [...prev.items];
      const hls = (next[idx].highlights || []).filter((_, i) => i !== hIdx);
      next[idx] = { ...next[idx], highlights: hls };
      return { ...prev, items: next };
    });
  };

  const handleAddBenefit = (idx) => {
    const text = (newBenefitText[idx] || '').trim();
    if (!text) return;
    setFormData(prev => {
      const next = [...prev.items];
      const bens = [...(next[idx].benefits || []), text];
      next[idx] = { ...next[idx], benefits: bens };
      return { ...prev, items: next };
    });
    setNewBenefitText(prev => ({ ...prev, [idx]: '' }));
    toast.success('Poin manfaat program ditambahkan');
  };

  const handleRemoveBenefit = (idx, bIdx) => {
    setFormData(prev => {
      const next = [...prev.items];
      const bens = (next[idx].benefits || []).filter((_, i) => i !== bIdx);
      next[idx] = { ...next[idx], benefits: bens };
      return { ...prev, items: next };
    });
  };

  const handleAddRequirement = (idx) => {
    const text = (newReqText[idx] || '').trim();
    if (!text) return;
    setFormData(prev => {
      const next = [...prev.items];
      const reqs = [...(next[idx].requirements || []), text];
      next[idx] = { ...next[idx], requirements: reqs };
      return { ...prev, items: next };
    });
    setNewReqText(prev => ({ ...prev, [idx]: '' }));
    toast.success('Poin persyaratan ditambahkan');
  };

  const handleRemoveRequirement = (idx, rIdx) => {
    setFormData(prev => {
      const next = [...prev.items];
      const reqs = (next[idx].requirements || []).filter((_, i) => i !== rIdx);
      next[idx] = { ...next[idx], requirements: reqs };
      return { ...prev, items: next };
    });
  };

  const handleAddOpportunity = () => {
    const newOpp = {
      id: `opportunity-${Date.now()}`,
      title: 'Program Magang / Relawan Baru',
      description: 'Pengalaman langsung mendukung pembelajaran dan kegiatan sekolah...',
      highlights: ['Pengalaman Lapangan', 'Bimbingan Mentor'],
      fullDescription: 'Penjelasan lengkap mengenai tujuan, tugas, serta manfaat yang didapatkan peserta...',
      duration: '1 Semester',
      commitment: '15-20 jam per minggu',
      benefits: ['Sertifikat resmi', 'Pengalaman kerja nyata', 'Relasi profesional'],
      requirements: ['Mahasiswa aktif / lulusan baru', 'Komunikatif dan berdedikasi'],
      contact: 'hr@permataharapan.sch.id',
      isNew: true
    };

    setFormData(prev => ({
      ...prev,
      items: [newOpp, ...prev.items]
    }));
    setExpandedIndex(0);
    toast.success('Program kesempatan baru ditambahkan ke draf. Isi data dan klik "Save Program Ini".');
  };

  const handleRemoveOpportunity = async (index) => {
    const itemToRemove = formData.items[index];

    // If item is new/draft not saved on server yet, remove locally
    if (itemToRemove.isNew && (!originalData.items || !originalData.items[index])) {
      setFormData(prev => ({
        ...prev,
        items: prev.items.filter((_, idx) => idx !== index)
      }));
      toast.info(`Program baru dibatalkan`);
      return;
    }

    if (formData.items.length <= 1) {
      toast.warning('Minimal harus ada 1 program kesempatan');
      return;
    }

    if (!window.confirm(`Ajukan penghapusan program "${itemToRemove.title || 'Untitled'}" ke Kepala Sekolah?`)) {
      return;
    }

    if (!sectionId) {
      toast.error('Section ID tidak ditemukan');
      return;
    }

    const key = `del_${index}`;
    setSubmitting(prev => ({ ...prev, [key]: true }));

    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `items.${index}.delete`,
          field_name: `Hapus Program: ${itemToRemove.title}`,
          old_value: originalData.items?.[index] || itemToRemove,
          new_value: { action: 'delete', id: itemToRemove.id, title: itemToRemove.title },
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`Pengajuan hapus program "${itemToRemove.title}" berhasil dikirim untuk approval`);
        setPendingDeletes(prev => ({ ...prev, [`item_${index}`]: true }));
        try {
          sessionStorage.removeItem('cms_page_careers');
          sessionStorage.removeItem('cms_sections_careers');
          window.dispatchEvent(new Event('cms-careers-updated'));
        } catch (e) {}
      } else {
        toast.error(result.error || result.message || 'Submission failed');
      }
    } catch (err) {
      console.error('[CareersOpportunitiesForm] Submit delete error:', err);
      toast.error('Gagal mengajukan hapus program: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleSubmitField = async (field, customValue, fieldDisplayName) => {
    if (!sectionId) {
      toast.error('Section ID tidak ditemukan');
      return;
    }

    const valToSubmit = customValue !== undefined ? customValue : formData[field];
    const oldVal = originalData[field];

    setSubmitting(prev => ({ ...prev, [field]: true }));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: field,
          field_name: fieldDisplayName || (field.charAt(0).toUpperCase() + field.slice(1)),
          old_value: oldVal,
          new_value: valToSubmit,
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`"${fieldDisplayName || field}" berhasil diajukan untuk approval`);
        setPendingFields(prev => ({ ...prev, [field]: true }));
        setOriginalData(prev => ({ ...prev, [field]: valToSubmit }));
        try {
          sessionStorage.removeItem('cms_page_careers');
          sessionStorage.removeItem('cms_sections_careers');
          window.dispatchEvent(new Event('cms-careers-updated'));
        } catch (e) {}
      } else {
        toast.error(result.error || result.message || 'Submission failed');
      }
    } catch (err) {
      console.error('[CareersOpportunitiesForm] Submit error:', err);
      toast.error('Error submitting change: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [field]: false }));
    }
  };

  const handleSubmitOpportunity = async (index) => {
    if (!sectionId) {
      toast.error('Section ID tidak ditemukan');
      return;
    }

    const item = formData.items[index];
    const key = `item_${index}`;
    const isNew = item.isNew || !originalData.items || !originalData.items[index];
    const fieldId = isNew ? `items.${index}.add` : `items.${index}.update`;

    setSubmitting(prev => ({ ...prev, [key]: true }));
    try {
      const cleanItem = { ...item };
      delete cleanItem.isNew;

      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: fieldId,
          field_name: `Opportunity: ${item.title || `Program ${index + 1}`}`,
          old_value: isNew ? null : (originalData.items?.[index] || {}),
          new_value: cleanItem,
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`Program "${item.title}" berhasil diajukan untuk approval`);
        setPendingItems(prev => ({ ...prev, [key]: true }));
        setOriginalData(prev => {
          const next = [...(prev.items || [])];
          next[index] = cleanItem;
          return { ...prev, items: next };
        });
        setFormData(prev => {
          const next = [...prev.items];
          next[index] = cleanItem;
          return { ...prev, items: next };
        });
        try {
          sessionStorage.removeItem('cms_page_careers');
          sessionStorage.removeItem('cms_sections_careers');
          window.dispatchEvent(new Event('cms-careers-updated'));
        } catch (e) {}
      } else {
        toast.error(result.error || result.message || 'Submission failed');
      }
    } catch (err) {
      console.error('[CareersOpportunitiesForm] Submit error:', err);
      toast.error('Gagal mengajukan program: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [key]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading Internships & Volunteering Section...</p>
      </div>
    );
  }

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Form Header */}
      <div className="unified-header">
        <div className="unified-header-icon">
          <GraduationCap size={22} />
        </div>
        <div>
          <h2 className="unified-title">Internships & Volunteering</h2>
          <p className="unified-subtitle">
            Kelola program magang, relawan acara, dan riset inovasi lengkap dengan durasi waktu, poin sorotan kartu, serta isi detail modal.
          </p>
        </div>
      </div>

      {/* Section Kicker */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Kicker</label>
          {pendingFields.kicker && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Approval
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.kicker}
            onChange={(e) => handleFieldChange('kicker', e.target.value)}
            disabled={pendingFields.kicker}
            placeholder="e.g. Internships / Volunteering"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('kicker', formData.kicker, 'Section Kicker')}
            disabled={pendingFields.kicker || submitting.kicker || formData.kicker === originalData.kicker}
          >
            <Save size={14} /> {submitting.kicker ? 'Saving...' : (pendingFields.kicker ? 'Pending' : 'Save')}
          </button>
        </div>
      </div>

      {/* Section Title */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Title</label>
          {pendingFields.title && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Approval
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.title}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            disabled={pendingFields.title}
            placeholder="e.g. Opportunities to Learn, Serve, and Grow"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('title', formData.title, 'Section Title')}
            disabled={pendingFields.title || submitting.title || formData.title === originalData.title}
          >
            <Save size={14} /> {submitting.title ? 'Saving...' : (pendingFields.title ? 'Pending' : 'Save')}
          </button>
        </div>
      </div>

      {/* Section Description */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Description</label>
          {pendingFields.description && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Approval
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <textarea
            className="unified-textarea"
            rows={2}
            value={formData.description}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            disabled={pendingFields.description}
            placeholder="Short-term roles that support the school community while building valuable experience..."
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('description', formData.description, 'Section Description')}
            disabled={pendingFields.description || submitting.description || formData.description === originalData.description}
          >
            <Save size={14} /> {submitting.description ? 'Saving...' : (pendingFields.description ? 'Pending' : 'Save')}
          </button>
        </div>
      </div>

      {/* Opportunities List Cards */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <div>
            <label className="unified-label">Daftar Program Magang & Relawan ({formData.items.length} Program)</label>
            <p className="unified-field-desc" style={{ marginTop: '4px' }}>
              Setiap program tampil sebagai kartu dengan sorotan utama serta modal interaktif saat tombol "Learn More" diklik pengunjung.
            </p>
          </div>
          <button
            type="button"
            className="unified-secondary-btn"
            onClick={handleAddOpportunity}
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <Plus size={16} /> Tambah Program Baru
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '16px' }}>
          {formData.items.map((item, idx) => {
            const isPending = pendingItems[`item_${idx}`];
            const isDeletePending = pendingDeletes[`item_${idx}`];
            const isSubmitting = submitting[`item_${idx}`] || submitting[`del_${idx}`];
            const isExpanded = expandedIndex === idx;

            return (
              <div
                key={idx}
                style={{
                  border: isDeletePending 
                    ? '1.5px dashed #ef4444' 
                    : (isPending ? '1.5px dashed #f59e0b' : '1px solid #e2e8f0'),
                  borderRadius: '14px',
                  background: isDeletePending ? '#fef2f2' : (isPending ? '#fffdf7' : '#ffffff'),
                  boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                  overflow: 'hidden'
                }}
              >
                {/* Accordion Card Header */}
                <div
                  style={{
                    padding: '16px 20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    background: isExpanded ? '#f8fafc' : '#ffffff',
                    borderBottom: isExpanded ? '1px solid #e2e8f0' : 'none'
                  }}
                  onClick={() => setExpandedIndex(isExpanded ? null : idx)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <span style={{ 
                      background: '#ede9fe', 
                      color: '#7c3aed', 
                      fontSize: '11px', 
                      fontWeight: 800, 
                      padding: '3px 10px', 
                      borderRadius: '9999px',
                      textTransform: 'uppercase'
                    }}>
                      PROGRAM #{idx + 1}
                    </span>
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>
                      {item.title || 'Program Tanpa Judul'}
                    </h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#64748b', fontSize: '13px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <Hourglass size={13} /> {item.duration}
                      </span>
                      <span>•</span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <Clock size={13} /> {item.commitment}
                      </span>
                      <span>•</span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <Mail size={13} /> {item.contact}
                      </span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {isDeletePending && (
                      <span className="unified-pending-pill" style={{ background: '#fee2e2', color: '#b91c1c' }}>
                        <Clock size={11} /> Pending Delete
                      </span>
                    )}
                    {!isDeletePending && isPending && (
                      <span className="unified-pending-pill">
                        <Clock size={12} /> Pending Approval
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveOpportunity(idx);
                      }}
                      disabled={isDeletePending || isSubmitting}
                      style={{ 
                        background: isDeletePending ? '#fee2e2' : 'none', 
                        border: 'none', 
                        color: '#ef4444', 
                        padding: '6px', 
                        cursor: isDeletePending ? 'not-allowed' : 'pointer',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: '11px'
                      }}
                      title="Hapus Program"
                    >
                      <Trash2 size={16} />
                      {isDeletePending && <span>Pending Delete</span>}
                    </button>
                    {isExpanded ? <ChevronUp size={18} color="#64748b" /> : <ChevronDown size={18} color="#64748b" />}
                  </div>
                </div>

                {/* Expanded Form Details */}
                {isExpanded && (
                  <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {/* Row 1: Title, Duration, Commitment, Contact */}
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '14px' }}>
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '6px', display: 'block' }}>
                          Judul Program *
                        </label>
                        <input
                          type="text"
                          className="unified-input"
                          value={item.title || ''}
                          disabled={isDeletePending || isPending}
                          onChange={(e) => handleItemChange(idx, 'title', e.target.value)}
                          placeholder="e.g. Student Teaching Internship"
                        />
                      </div>

                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '6px', display: 'block' }}>
                          Durasi (Duration)
                        </label>
                        <input
                          type="text"
                          className="unified-input"
                          value={item.duration || ''}
                          disabled={isDeletePending || isPending}
                          onChange={(e) => handleItemChange(idx, 'duration', e.target.value)}
                          placeholder="e.g. 1-2 semesters"
                        />
                      </div>

                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '6px', display: 'block' }}>
                          Komitmen Waktu
                        </label>
                        <input
                          type="text"
                          className="unified-input"
                          value={item.commitment || ''}
                          disabled={isDeletePending || isPending}
                          onChange={(e) => handleItemChange(idx, 'commitment', e.target.value)}
                          placeholder="e.g. 20-30 hours per week"
                        />
                      </div>

                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '6px', display: 'block' }}>
                          Email Kontak HR *
                        </label>
                        <input
                          type="email"
                          className="unified-input"
                          value={item.contact || ''}
                          disabled={isDeletePending || isPending}
                          onChange={(e) => handleItemChange(idx, 'contact', e.target.value)}
                          placeholder="e.g. hr@permataharapan.sch.id"
                        />
                      </div>
                    </div>

                    {/* Row 2: Description on Card (Lead) */}
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '6px', display: 'block' }}>
                        Deskripsi Singkat Kartu (Tampil di preview kartu halaman)
                      </label>
                      <input
                        type="text"
                        className="unified-input"
                        value={item.description || ''}
                        disabled={isDeletePending || isPending}
                        onChange={(e) => handleItemChange(idx, 'description', e.target.value)}
                        placeholder="Gain hands-on teaching experience in a supportive environment with mentorship..."
                      />
                    </div>

                    {/* Row 3: Full Description (Modal View) */}
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '6px', display: 'block' }}>
                        Deskripsi Lengkap Modal (Tampil di modal "About This Opportunity")
                      </label>
                      <textarea
                        className="unified-textarea"
                        rows={2}
                        value={item.fullDescription || ''}
                        disabled={isDeletePending || isPending}
                        onChange={(e) => handleItemChange(idx, 'fullDescription', e.target.value)}
                        placeholder="Our Student Teaching Internship program is designed for aspiring educators who want to gain practical classroom experience..."
                      />
                    </div>

                    {/* Row 4: Card Highlights */}
                    <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                      <label style={{ fontSize: '12px', fontWeight: 700, color: '#1e293b', marginBottom: '8px', display: 'block' }}>
                        Poin Sorotan Kartu (Card Highlights)
                      </label>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '10px' }}>
                        {(item.highlights || []).map((hl, hIdx) => (
                          <div key={hIdx} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#ffffff', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                            <CheckCircle2 size={14} color="#7c3aed" />
                            <span style={{ fontSize: '13px', color: '#1e293b', flex: 1 }}>{hl}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveHighlight(idx, hIdx)}
                              disabled={isDeletePending || isPending}
                              style={{ background: 'none', border: 'none', color: '#ef4444', cursor: (isDeletePending || isPending) ? 'not-allowed' : 'pointer', padding: 0 }}
                              title="Hapus sorotan"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        ))}
                      </div>

                      <div style={{ display: 'flex', gap: '8px' }}>
                        <input
                          type="text"
                          className="unified-input"
                          value={newHighlightText[idx] || ''}
                          disabled={isDeletePending || isPending}
                          onChange={(e) => setNewHighlightText(prev => ({ ...prev, [idx]: e.target.value }))}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddHighlight(idx);
                            }
                          }}
                          placeholder="Tulis poin sorotan baru..."
                          style={{ fontSize: '12px', padding: '8px 12px' }}
                        />
                        <button
                          type="button"
                          className="unified-secondary-btn"
                          disabled={isDeletePending || isPending}
                          onClick={() => handleAddHighlight(idx)}
                          style={{ fontSize: '12px', whiteSpace: 'nowrap' }}
                        >
                          + Tambah Sorotan
                        </button>
                      </div>
                    </div>

                    {/* Row 5: Benefits List ("What You'll Gain") */}
                    <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                      <label style={{ fontSize: '12px', fontWeight: 700, color: '#1e293b', marginBottom: '8px', display: 'block' }}>
                        Manfaat & Nilai Tambah (What You'll Gain di Modal)
                      </label>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '10px' }}>
                        {(item.benefits || []).map((ben, bIdx) => (
                          <div key={bIdx} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#ffffff', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                            <Star size={14} color="#f59e0b" />
                            <span style={{ fontSize: '13px', color: '#1e293b', flex: 1 }}>{ben}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveBenefit(idx, bIdx)}
                              disabled={isDeletePending || isPending}
                              style={{ background: 'none', border: 'none', color: '#ef4444', cursor: (isDeletePending || isPending) ? 'not-allowed' : 'pointer', padding: 0 }}
                              title="Hapus manfaat"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        ))}
                      </div>

                      <div style={{ display: 'flex', gap: '8px' }}>
                        <input
                          type="text"
                          className="unified-input"
                          value={newBenefitText[idx] || ''}
                          disabled={isDeletePending || isPending}
                          onChange={(e) => setNewBenefitText(prev => ({ ...prev, [idx]: e.target.value }))}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddBenefit(idx);
                            }
                          }}
                          placeholder="Tulis manfaat baru (misal: 'Certificate of completion')..."
                          style={{ fontSize: '12px', padding: '8px 12px' }}
                        />
                        <button
                          type="button"
                          className="unified-secondary-btn"
                          disabled={isDeletePending || isPending}
                          onClick={() => handleAddBenefit(idx)}
                          style={{ fontSize: '12px', whiteSpace: 'nowrap' }}
                        >
                          + Tambah Manfaat
                        </button>
                      </div>
                    </div>

                    {/* Row 6: Requirements List ("What We're Looking For") */}
                    <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                      <label style={{ fontSize: '12px', fontWeight: 700, color: '#1e293b', marginBottom: '8px', display: 'block' }}>
                        Persyaratan & Kriteria (What We're Looking For di Modal)
                      </label>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '10px' }}>
                        {(item.requirements || []).map((req, qIdx) => (
                          <div key={qIdx} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#ffffff', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                            <Check size={14} color="#059669" />
                            <span style={{ fontSize: '13px', color: '#1e293b', flex: 1 }}>{req}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveRequirement(idx, qIdx)}
                              disabled={isDeletePending || isPending}
                              style={{ background: 'none', border: 'none', color: '#ef4444', cursor: (isDeletePending || isPending) ? 'not-allowed' : 'pointer', padding: 0 }}
                              title="Hapus kriteria"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        ))}
                      </div>

                      <div style={{ display: 'flex', gap: '8px' }}>
                        <input
                          type="text"
                          className="unified-input"
                          value={newReqText[idx] || ''}
                          disabled={isDeletePending || isPending}
                          onChange={(e) => setNewReqText(prev => ({ ...prev, [idx]: e.target.value }))}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddRequirement(idx);
                            }
                          }}
                          placeholder="Tulis kriteria baru..."
                          style={{ fontSize: '12px', padding: '8px 12px' }}
                        />
                        <button
                          type="button"
                          className="unified-secondary-btn"
                          disabled={isDeletePending || isPending}
                          onClick={() => handleAddRequirement(idx)}
                          style={{ fontSize: '12px', whiteSpace: 'nowrap' }}
                        >
                          + Tambah Persyaratan
                        </button>
                      </div>
                    </div>

                    {/* Card Submit Action */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '10px', borderTop: '1px solid #e2e8f0' }}>
                      <button
                        type="button"
                        className="unified-submit-button"
                        onClick={() => handleSubmitOpportunity(idx)}
                        disabled={isDeletePending || isPending || isSubmitting}
                      >
                        <Save size={14} />
                        {isSubmitting ? 'Saving...' : (isDeletePending ? 'Pending Delete' : (isPending ? 'Pending' : 'Save Program Ini'))}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
